import axios from "axios";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { authTokenService } from "../../localStorage/accessToken";
import { apiClient, reloadHeaders } from "../../services/api";
import { authLogin, getTokenStatus } from "../../services/auth/authServices";

interface AuthContextType {
  isLoggedIn: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    !!authTokenService.get()
  );

  const login = async (username: string, password: string) => {
    try {
      const response = await authLogin({ username, password });
      if (response?.access_token) {
        authTokenService.set(response.access_token);
        reloadHeaders();
        setIsLoggedIn(true);
        navigate("/landing");
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("Login failed. Please check your credentials.");
    }
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      if (isLoggedIn) {
        await getTokenStatus();
      }
    }, 10 * 60 * 1000);

    const interceptor = apiClient.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          alert("Your session has expired. Please log in again.");
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.instance.interceptors.response.eject(interceptor);
      clearInterval(interval);
    };
  }, [isLoggedIn]);

  const logout = () => {
    authTokenService.remove();
    reloadHeaders();
    setIsLoggedIn(false);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
